import React, { useMemo } from "react";
import { CommandLiveContext } from "../../pages/CommandCentrePage";
import { CommandKPIHeader } from "../../components/command/CommandKPIHeader";
import { PriorityDeals } from "../../components/command/PriorityDeals";

/* ─────────────────────────────────────────────────────────────
   MarketingFlowCentre
   Phase 1 Homepage · Hero product surface

   Composes REAL production Command Centre primitives
   (CommandKPIHeader + PriorityDeals) inside a marketing frame
   by providing a static CommandLiveContext payload.

   The primitives read their state via useContext(CommandLiveContext).
   They do NOT call useCommandLive() themselves — that hook lives
   in CommandCentrePage. By providing our own context value here,
   the primitives render authentic UI with curated demo data and
   ZERO API calls, ZERO auth dependency.

   NOTHING inside /components/command/* or CommandCentrePage.jsx
   is modified. The marketing wrapper only supplies:
     • framing (rounded surface, subtle chrome)
     • scale (max-width, clipping)
     • ambient glow
     • the demo context payload

   Product terminology stays exactly as in the real product:
     Priority deals · Outstanding tasks · Exchanges this week · Active deals
   ───────────────────────────────────────────────────────────── */

/* Curated demo payload — shaped so the primitives' OWN
   derivation naturally produces the target numbers:
     • 23 active deals
     • 8 priority deals (next_best_action.mode === "action_needed")
     • 3 exchanges this week (target_exchange within next 7 days)
     • 3 at-risk (risk: "high") visible in Priority Deals
     • 23 Hallings Wharf surfaces first with "Management pack overdue"

   Determinism note: target_exchange values are rebased on each
   mount via a factory (see buildDemoDeals()) so the KPI header's
   `Date.now()` filter always produces the SAME approved counts
   regardless of visit date/time. Only 3 deals sit inside the
   `now .. now + 7 days` window; every other deal is placed well
   outside so the "Exchanges this week: 3" figure is stable.
*/
const DAY = 24 * 60 * 60 * 1000;

const _demoDeal = (o) => ({
  id: o.id,
  property_address: o.address,
  address: o.address,
  buyer_name: o.buyer || "—",
  seller_name: o.seller || "—",
  stage: o.stage,
  progress: o.progress,
  risk: o.risk,
  chain_health: o.chain_health || "stable",
  target_exchange: o.target_exchange,
  status: "active",
  thumb: null,
  image: null,
  next_best_action: o.nba,
});

/* Deal blueprints — target_exchange stored as an offset (days
   from mount). buildDemoDeals() converts each offset to an ISO
   date relative to the render's current time so the set is
   deterministic on every mount. Exactly 3 deals land inside the
   7-day window (offsets: 3, 5, 6). */
const DEAL_BLUEPRINTS = [
  { id: "mkt-01", address: "23 Hallings Wharf, Stratford, London E15", buyer: "Mr S. Patel", seller: "Ms J. Carter",
    stage: "Enquiries · Management pack overdue", progress: 72, risk: "high", chain_health: "at_risk", exchangeIn: 9,
    nba: { mode: "action_needed", label: "Chase management pack — 3d overdue", reason: "Freeholder unresponsive for 3 days · exchange target in 9 days" } },
  { id: "mkt-02", address: "14 Priory Lane, Wimbledon SW19", buyer: "Mr & Mrs Ahmed", seller: "Mr T. Roberts",
    stage: "Awaiting solicitor response", progress: 64, risk: "high", chain_health: "elevated", exchangeIn: 3,
    nba: { mode: "action_needed", label: "Solicitor inactive — 4d without reply", reason: "No solicitor response on enquiries · exchange in 3 days" } },
  { id: "mkt-03", address: "7 Bolton Road, Chiswick W4", buyer: "Ms E. Harper", seller: "Mr J. Whitmore",
    stage: "AML pending", progress: 58, risk: "high", chain_health: "at_risk", exchangeIn: 5,
    nba: { mode: "action_needed", label: "AML documents outstanding · escalate", reason: "Buyer ID missing for 5 days · exchange in 5 days" } },
  { id: "mkt-04", address: "10 High Street, Richmond TW9", buyer: "Ms R. Chen", seller: "The Kearney Family",
    stage: "Searches overdue", progress: 55, risk: "med", exchangeIn: 12,
    nba: { mode: "action_needed", label: "Chase local authority searches", reason: "Council response overdue 8 days" } },
  { id: "mkt-05", address: "27 Tudor Street, Islington N1", buyer: "Mr D. Okafor", seller: "Mr & Mrs Lewis",
    stage: "Draft contract review", progress: 68, risk: "med", exchangeIn: 6,
    nba: { mode: "action_needed", label: "Contract amends outstanding", reason: "Vendor solicitor to return amendments" } },
  { id: "mkt-06", address: "41 Station Road, Kew TW9", buyer: "Mr F. Taylor", seller: "Ms L. Bishop",
    stage: "Mortgage valuation pending", progress: 46, risk: "med", exchangeIn: 18,
    nba: { mode: "action_needed", label: "Broker chase — valuation booked?", reason: "Mortgage valuation not yet booked" } },
  { id: "mkt-07", address: "8 Park Road, Ealing W13", buyer: "Mr K. Sharma", seller: "The Newman Trust",
    stage: "TA6 outstanding", progress: 38, risk: "med", exchangeIn: 21,
    nba: { mode: "action_needed", label: "TA6 forms outstanding from vendor", reason: "Vendor TA6 not returned" } },
  { id: "mkt-08", address: "18 Heritage Mews, Notting Hill W11", buyer: "Ms A. Blackwood", seller: "Mr P. Vance",
    stage: "Enquiries raised", progress: 44, risk: "med", exchangeIn: 15,
    nba: { mode: "action_needed", label: "3 enquiries pending solicitor reply", reason: "Enquiries raised 6 days ago · awaiting response" } },
  { id: "mkt-09", address: "3 Marlborough Avenue, Fulham SW6", buyer: "Mr J. Pryce", seller: "Ms C. Fenwick",
    stage: "Awaiting mortgage offer", progress: 61, risk: "low", exchangeIn: 28,
    nba: { mode: "waiting", label: "Awaiting lender offer", reason: "Lender review in progress" } },
  { id: "mkt-10", address: "62 Cambridge Road, Kingston KT1", buyer: "Mr & Mrs Gill", seller: "The Ashford Family",
    stage: "Searches ordered", progress: 40, risk: "low", exchangeIn: 34,
    nba: { mode: "waiting", label: "Awaiting search results", reason: "Local authority searches ordered" } },
  { id: "mkt-11", address: "5 Cornwall Gardens, Kensington SW7", buyer: "Mr T. Vasquez", seller: "Ms E. Rowe",
    stage: "Awaiting survey", progress: 52, risk: "low", exchangeIn: 30,
    nba: { mode: "waiting", label: "Awaiting survey report", reason: "Survey booked, report due" } },
  { id: "mkt-12", address: "11 Church Road, Barnes SW13", buyer: "Ms H. Anand", seller: "Mr & Mrs Whyte",
    stage: "Ready for exchange", progress: 88, risk: "low", exchangeIn: 11,
    nba: { mode: "next_best_action", label: "Confirm exchange date", reason: "All conditions cleared" } },
  { id: "mkt-13", address: "44 Elm Grove, Wandsworth SW18", buyer: "Mr N. Rossi", seller: "Ms M. Delahaye",
    stage: "Contracts signed", progress: 82, risk: "low", exchangeIn: 14,
    nba: { mode: "next_best_action", label: "Coordinate exchange window", reason: "Signed contracts in hand" } },
  { id: "mkt-14", address: "9 Waverley Road, Highgate N6", buyer: "Mr O. Bright", seller: "The Cavendish Estate",
    stage: "Mortgage offer received", progress: 74, risk: "low", exchangeIn: 20,
    nba: { mode: "next_best_action", label: "Book exchange call", reason: "All parties aligned" } },
  { id: "mkt-15", address: "16 Green Lanes, Palmers Green N13", stage: "Under offer", progress: 30, risk: "low", exchangeIn: 40, nba: { mode: "next_best_action", label: "Progress to searches" } },
  { id: "mkt-16", address: "22 Prince Regent Lane, Greenwich SE10", stage: "AML complete", progress: 42, risk: "low", exchangeIn: 38, nba: { mode: "next_best_action", label: "Order local authority searches" } },
  { id: "mkt-17", address: "5 King Edward Avenue, Muswell Hill N10", stage: "Memo issued", progress: 25, risk: "low", exchangeIn: 45, nba: { mode: "next_best_action", label: "Instruct solicitor" } },
  { id: "mkt-18", address: "28 Belsize Park, Hampstead NW3", stage: "Under offer", progress: 22, risk: "low", exchangeIn: 50, nba: { mode: "next_best_action", label: "Verify proof of funds" } },
  { id: "mkt-19", address: "12 Camberwell Grove, Camberwell SE5", stage: "Instructed", progress: 18, risk: "low", exchangeIn: 55, nba: { mode: "next_best_action", label: "Kick off Memorandum of Sale" } },
  { id: "mkt-20", address: "4 Blackheath Rise, Blackheath SE13", stage: "Offer accepted", progress: 15, risk: "low", exchangeIn: 60, nba: { mode: "next_best_action", label: "Issue Memorandum of Sale" } },
  { id: "mkt-21", address: "7 Hillcrest Road, Ealing W5", stage: "Under offer", progress: 12, risk: "low", exchangeIn: 65, nba: { mode: "next_best_action", label: "Confirm buyer position" } },
  { id: "mkt-22", address: "31 Fairfax Place, Hampstead NW6", stage: "Under offer", progress: 10, risk: "low", exchangeIn: 70, nba: { mode: "next_best_action", label: "Chase buyer solicitor instruction" } },
  { id: "mkt-23", address: "19 Southbank Terrace, Waterloo SE1", stage: "New instruction", progress: 5, risk: "low", exchangeIn: 80, nba: { mode: "next_best_action", label: "Confirm marketing plan" } },
];

const buildDemoDeals = () => {
  const now = Date.now();
  return DEAL_BLUEPRINTS.map((b) =>
    _demoDeal({
      ...b,
      target_exchange: new Date(now + b.exchangeIn * DAY).toISOString(),
    }),
  );
};

const DEMO_TODAY = {
  items: [
    { id: "t1", title: "Chase management pack — 23 Hallings Wharf" },
    { id: "t2", title: "Call solicitor — 14 Priory Lane" },
    { id: "t3", title: "Escalate AML — 7 Bolton Road" },
    { id: "t4", title: "Chase local authority — 10 High Street" },
    { id: "t5", title: "Draft contract amends — 27 Tudor Street" },
    { id: "t6", title: "Broker follow-up — 41 Station Road" },
    { id: "t7", title: "TA6 chase — 8 Park Road" },
    { id: "t8", title: "Enquiries follow-up — 18 Heritage Mews" },
    { id: "t9", title: "Confirm exchange date — 11 Church Road", completed_at: null },
    { id: "t10", title: "Coordinate exchange — 44 Elm Grove" },
    { id: "t11", title: "Book exchange call — 9 Waverley Road" },
    { id: "t12", title: "Progress to searches — 16 Green Lanes" },
  ],
};

const DEMO_ME = {
  display_name: "Emma",
  name: "Emma Parker",
  email: "emma@chainflow.app",
  branch: "London Bridge",
  role: "Sales Manager",
};

export const MarketingFlowCentre = () => {
  const value = useMemo(
    () => ({
      me: DEMO_ME,
      deals: buildDemoDeals(),
      today: DEMO_TODAY,
      loading: false,
      error: null,
      refresh: () => {},
    }),
    [],
  );

  return (
    <CommandLiveContext.Provider value={value}>
      <div
        className="relative rounded-2xl overflow-hidden bg-white"
        style={{
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow:
            "0 60px 120px -30px rgba(15,23,42,0.55), 0 24px 60px -20px rgba(60,40,180,0.35), 0 0 0 1px rgba(139,92,246,0.10)",
        }}
        data-testid="marketing-flow-centre"
        aria-label="ChainFlow Flow Centre preview"
      >
        {/* Browser chrome — subtle, restrained */}
        <div
          className="flex items-center gap-2 h-8 px-4 border-b"
          style={{
            background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
            borderColor: "rgba(15,23,42,0.06)",
          }}
        >
          <span className="w-2.5 h-2.5 rounded-full bg-rose-300/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-300/80" />
          <span className="ml-3 text-[10.5px] font-semibold text-slate-500 tracking-wide">
            app.chainflowos.com / command-centre
          </span>
          <span className="ml-auto text-[10px] font-semibold text-slate-400 inline-flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 cf-pulse" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
            </span>
            Live
          </span>
        </div>

        {/* Real product surface — no restyling.
            On narrow viewports the whole composition is
            capped so KPIs + first Priority Deal (23 Hallings
            Wharf) are the guaranteed visible content, with a
            bottom fade hinting continuation. */}
        <div
          className="relative max-h-[860px] sm:max-h-[920px] md:max-h-none overflow-hidden"
          style={{ background: "var(--cf-app-bg, #F7F8FB)" }}
        >
          <CommandKPIHeader />
          <div className="px-4 md:px-8 py-6">
            <PriorityDeals />
          </div>
          {/* Bottom fade to hint content continues in-product */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16"
            style={{
              background: "linear-gradient(180deg, rgba(247,248,251,0) 0%, rgba(247,248,251,0.95) 100%)",
            }}
          />
        </div>
      </div>
    </CommandLiveContext.Provider>
  );
};

export default MarketingFlowCentre;
